/**
 * Survey Stimulus.
 *
 * @author Alain Pitiot and Nikita Agafonov
 * @copyright (c) 2020-2025 Open Science Tools Ltd. (https://opensciencetools.org)
 * @license Distributed under the terms of the MIT License
 */

import * as PIXI from "pixi.js-legacy";
import { VisualStim } from "./VisualStim.js";
import * as util from "../util/Util.js";
import {Clock} from "../util/Clock.js";
import {ExperimentHandler} from "../data/ExperimentHandler.js";

// PsychoJS SurveyJS extensions:
import registerSelectBoxWidget from "./survey/widgets/SelectBox.js";
import registerSliderWidget from "./survey/widgets/SliderWidget.js";
import registerSideBySideMatrix from "./survey/widgets/SideBySideMatrix.js";
import registerMaxDiffMatrix from "./survey/widgets/MaxDiffMatrix.js";
import registerSliderStar from "./survey/widgets/SliderStar.js";
import MatrixBipolar from "./survey/components/MatrixBipolar.js";
import DropdownExtensions from "./survey/components/DropdownExtensions.js";
import customExpressionFunctionsArray from "./survey/extensions/customExpressionFunctions.js";
import showdown from "showdown";


/**
 * Survey Stimulus.
 *
 * @extends VisualStim
 */
export class Survey extends VisualStim
{
	static SURVEY_EXPERIMENT_PARAMETERS = ["surveyId", "showStartDialog", "showEndDialog", "completionUrl", "cancellationUrl", "quitOnEsc"];

	static NODE_TYPE =
	{
		QUESTION_BLOCK: "QUESTION_BLOCK",
		CONDITIONAL: "IF_THEN_ELSE_GROUP",
		EMBEDDED_DATA: "VARIABLES",
		RANDOMIZER: "RANDOM_GROUP",
		SEQUENTIAL: "SEQUENTIAL_GROUP",
		ENDSURVEY: "END"
	};

	static CAPTIONS =
	{
		NEXT: "Next"
	};

	static SURVEY_EVENT =
	{
		// move onto the next node, in the survey flow:
		NEXT_NODE: 0,

		// move onto the previous node, in the survey flow:
		PREV_NODE: 1,

		// skip to the end of the block:
		SKIP_TO_END_OF_BLOCK: 3,

		// skip to the end of the survey flow:
		SKIP_TO_END_OF_SURVEY: 4
	};

	// TODO we should get rid of NODE_EXIT_CODES entirely, and use only SURVEY_EVENT instead
	static NODE_EXIT_CODES =
	{
		NORMAL: 0,
		BREAK_FLOW: 1
	};

	/**
	 * @memberOf module:visual
	 * @param {Object} options
	 * @param {String} options.name - the name used when logging messages from this stimulus
	 * @param {Window} options.win - the associated Window
	 * @param {string} [options.surveyId] - the survey id
	 * @param {Object | string} [options.model] - the survey model
	 * @param {string} [options.units= "norm"] - the units of the stimulus (e.g. for size, position, vertices)
	 * @param {Array.<number>} [options.pos= [0, 0]] - the position of the center of the stimulus
	 * @param {number} [options.ori= 0.0] - the orientation (in degrees)
	 * @param {number} [options.size] - the size of the rendered survey
	 * @param {number} [options.depth= 0] - the depth (i.e. the z order)
	 * @param {boolean} [options.autoDraw= false] - whether  the stimulus should be automatically drawn
	 * 	on every frame flip
	 * @param {boolean} [options.autoLog= false] - whether to log
	 */
	constructor({ name, win, model, surveyId, saveOnPageChange, pos, units, ori, size, depth, autoDraw, autoLog } = {})
	{
		super({ name, win, units, ori, depth, pos, size, autoDraw, autoLog });

		// Storing all existing signaturePad questions to properly handle their resize.
		// Unfortunately signaturepad question type can't handle resizing properly by itself.
		this._signaturePads = [];

		// whether the user is done with the survey, independently of whether the survey is fully completed:
		this.isFinished = false;

		// accumulated completion flag updated after each survey node is completed
		// note: 	this make it possible to track completion as we move through the survey flow.
		// 				_isCompletedAll will be flipped to false whenever a survey node is not completed
		this._isCompletedAll = true;

		// timestamps associated to each question:
		this._questionAnswerTimestamps = {};
		// timestamps clock:
		this._questionAnswerTimestampClock = new Clock();

		this._overallSurveyResults = {};

		// the super-flow survey
		// note: surveyModel is either a copy of model if model is a super-flow survey, or it is a super-flow version
		// of model
		this._surveyModel = undefined;
		// the currently running surveyJS survey
		this._surveyJSModel = undefined;

		this._expressionsRunner = undefined;
		this._lastPageSwitchHandledIdx = -1;
		this._variables = {};

		// the promise associated with the running of a flow's QUESTION_BLOCK, i.e. a SurveyJS survey
		this._blockPromise = undefined;
		this._blockPromiseResolve = undefined;
		this._blockPromiseReject = undefined;

		// callback triggered when the user is done with the survey: nothing to do by default
		this._onFinishedCallback = () => {};

		// init SurveyJS:
		this._initSurveyJS();

		// default size:
		if (typeof size === "undefined")
		{
			this.size = (this.unit === "norm") ? [2.0, 2.0] : [1.0, 1.0];
		}

		this._addAttribute("model", model);
		this._addAttribute("saveOnPageChange", saveOnPageChange, false);

		// the default surveyId is an uuid based on the experiment id (or name) and the survey name:
		// this way, it is always the same within a given experiment
		this._hasSelfGeneratedSurveyId = (typeof surveyId === "undefined");
		const defaultSurveyId = (this._psychoJS.getEnvironment() === ExperimentHandler.Environment.SERVER) ?
			util.makeUuid(`${name}@${this._psychoJS.config.gitlab.projectId}`) :
			util.makeUuid(`${name}@${this._psychoJS.config.experiment.name}`);
		this._addAttribute(
			"surveyId",
			surveyId,
			defaultSurveyId
		);

		// estimate the bounding box:
		this._estimateBoundingBox();

		// prepare a Markdown to HTML converter:
		this._mdConverter = new showdown.Converter();
		this._mdConverter.setFlavor('vanilla');

		if (this._autoLog)
		{
			this._psychoJS.experimentLogger.exp(`Created ${this.name} = ${this.toString()}`);
		}
	}

	get isCompleted ()
	{
		return this.isFinished && this._isCompletedAll;
	}

	/**
	 * Setter for the model attribute.
	 *
	 * @param {Object | string} model - the survey model
	 * @param {boolean} [log= false] - whether to log
	 * @return {void}
	 */
	setModel(model, log = false)
	{
		const response = {
			origin: "Survey.setModel",
			context: `when setting the model of Survey: ${this._name}`,
		};

		try
		{
			// model is undefined: that's fine, but we raise a warning in case this is a symptom of an actual problem
			if (typeof model === "undefined")
			{
				this.psychoJS.logger.warn(`setting the model of Survey: ${this._name} with argument: undefined.`);
				this.psychoJS.logger.debug(`set the model of Survey: ${this._name} as: undefined`);
			}
			else
			{
				// model is a string: it should be the name of a resource, which we load
				if (typeof model === "string")
				{
					const encodedModel = this.psychoJS.serverManager.getResource(model);
					const decodedModel = new TextDecoder("utf-8").decode(encodedModel);
					model = JSON.parse(decodedModel);

					// make sure that [Previous] buttons appear by default:
					if (!('surveySettings' in model))
					{
						model['surveySettings'] = { showPrevButton: true };
					}
					else
					{
						model['surveySettings']['showPrevButton'] = true;
					}
				}

				// model should now be an object:
				if (typeof model !== "object")
				{
					throw "model is neither the name of a resource nor an object";
				}

				// if model is a straight-forward SurveyJS model, instead of a Pavlovia Survey super-flow model,
				// convert it:
				if (!('surveyFlow' in model))
				{
					const surveyName = ("surveyName" in model) ? model.surveyName : "<unknown survey>";

					model = {
						surveyName,
						surveys: [model],
						embeddedData: [],
						// surveysMap: {},
						// questionMapsBySurvey: {},
						surveyFlow: {
							name: "root",
							type: "SEQUENTIAL_GROUP",
							nodes: [{
								type: "QUESTION_BLOCK",
								name: "block_1",
								surveyIdx: 0
							}]
						},

						surveySettings: { showPrevButton: true },

						// surveyRunLogic: {},
						inQuestionRandomization: {},
						questionsOrderRandomization: [],
						questionSkipLogic: {},

						questionsConverted: -1,
						questionsTotal: -1,
						logs: []
					};

					this.psychoJS.logger.debug(`converted the legacy model to the new super-flow model: ${JSON.stringify(model)}`);
				}

				// mark the root (top-most) node:
				model.surveyFlow.isRootNode = true;

				this._surveyModel = model;

				// augment the question names with block names:
				// this._augmentQuestionNames();

				this._setAttribute("model", model, log);
				this._onChange(true, true)();
			}
		}
		catch (error)
		{
			throw { ...response, error };
		}
	}

	/**
	 * Setter for the surveyId attribute.
	 *
	 * @param {string} surveyId - the survey Id
	 * @param {boolean} [log= false] - whether to log
	 * @return {void}
	 */
	setSurveyId(surveyId, log = false)
	{
		this._setAttribute("surveyId", surveyId, log);

		// only update the model if a genuine surveyId was given as parameter to the Survey:
		if (!this._hasSelfGeneratedSurveyId)
		{
			this.setModel(`${surveyId}.sid`, log);
		}
	}

	/**
	 * Set survey variables.
	 *
	 * @param {Object} variables - an object with a number of variable name/variable value pairs
	 * @param {string[]} [excludedNames={}] - excluded variable names
	 * @return {void}
	 */
	setVariables(variables, excludedNames)
	{
		// filter the variables and set them:
		// const filteredVariables = {};
		// for (const name in variables)
		// {
		// 	if (excludedNames.indexOf(name) === -1)
		// 	{
		// 		filteredVariables[name] = variables[name];
		// 		this._surveyModel.setVariable(name, variables[name]);
		// 	}
		// }

		// // set the values:
		// this._surveyModel.mergeData(filteredVariables);

		for (const name in variables)
		{
			if (excludedNames.indexOf(name) === -1)
			{
				this._variables[name] = variables[name];
				// this._surveyModel.variables[name] = variables[name];
			}
		}
	}

	/**
	 * Evaluate an expression, taking into account the survey responses.
	 *
	 * @param {string} expression - the expression to evaluate
	 * @returns {any} the evaluated expression
	 */
	evaluateExpression(expression)
	{
		if (typeof expression === "undefined" || typeof this._surveyJSModel === "undefined")
		{
			return undefined;
		}

		// modify the expression when it is a simple URL, without variables
		// i.e. when there is no quote and no brackets
		if (expression.indexOf("'") === -1 && expression.indexOf("{") === -1)
		{
			expression = `'${expression}'`;
		}

		return this._surveyJSModel.runExpression(expression);
	}

	/**
	 * Add a callback that will be triggered when the participant finishes the survey.
	 *
	 * @param callback - callback triggered when the participant finishes the survey
	 * @return {void}
	 */
	onFinished(callback)
	{
		if (typeof this._surveyModel === "undefined")
		{
			throw {
				origin: "Survey.onFinished",
				context: "when setting a callback triggered when the participant finishes the survey",
				error: "the survey does not have a model"
			};
		}

		// note: we cannot simply add the callback to surveyModel.onComplete since we first need
		// to run _onSurveyComplete in order to collect data, estimate whether the survey is complete, etc.
		if (typeof callback === "function")
		{
			this._onFinishedCallback = callback;
		}
		// this._surveyModel.onComplete.add(callback);
	}

	/**
	 * Get the survey response.
	 */
	getResponse()
	{
		return this._overallSurveyResults;
	}

	/**
	 * Upload the survey response to the pavlovia.org server.
	 *
	 * @returns {Promise<ServerManager.UploadDataPromise>} a promise resolved when the survey response
	 * 	has been saved
	 */
	save()
	{
		this._psychoJS.logger.info("[PsychoJS] Save survey response.");

		// get the survey response and complement it with experimentInfo fields:
		const response = this.getResponse();
		for (const field in this.psychoJS.experiment.extraInfo)
		{
			if (Survey.SURVEY_EXPERIMENT_PARAMETERS.indexOf(field) === -1)
			{
				response[field] = this.psychoJS.experiment.extraInfo[field];
			}
		}

		// add timing information:
		for (const question in this._questionAnswerTimestamps)
		{
			response[`${question}_rt`] = this._questionAnswerTimestamps[question].timestamp;
		}

		// sort the questions and question response times alphabetically:
		const sortedResponses = Object.keys(response).sort().reduce( (sorted, key) =>
			{
				sorted[key] = response[key];
				return sorted;
			},
			{}
		);

		// if the response cannot be uploaded, e.g. the experiment is running locally, or
		// if it is piloting mode, then we offer the response as a file for download:
		if (this._psychoJS.getEnvironment() !== ExperimentHandler.Environment.SERVER ||
			this._psychoJS.config.experiment.status !== "RUNNING" ||
			this._psychoJS._serverMsg.has("__pilotToken"))
		{
			const filename = `survey_${this._surveyId}.json`;
			const blob = new Blob([JSON.stringify(sortedResponses)], { type: "application/json" });

			const anchor = document.createElement("a");
			anchor.href = window.URL.createObjectURL(blob);
			anchor.download = filename;
			document.body.appendChild(anchor);
			anchor.click();
			document.body.removeChild(anchor);

			return Promise.resolve({});
		}

		// otherwise, we do upload the survey response
		// note: if the surveyId was self-generated instead of being a parameter of the constructor,
		// we need to also upload the survey model, as a new survey might need to be created on the fly
		// by the server for this experiment.
		if (!this._hasSelfGeneratedSurveyId)
		{
			return this._psychoJS.serverManager.uploadSurveyResponse(
				this._surveyId, sortedResponses, this.isCompleted
			);
		}
		else
		{
			return this._psychoJS.serverManager.uploadSurveyResponse(
				this._surveyId, sortedResponses, this.isCompleted, this._surveyModel
			);
		}
	}

	/**
	 * Hide this stimulus on the next frame draw.
	 *
	 * @override
	 * @note We over-ride MinimalStim.hide such that we can remove the survey DOM element
	 */
	hide()
	{
		const surveyDiv = document.getElementById(this._surveyDivId);
		if (surveyDiv !== null)
		{
			document.body.removeChild(surveyDiv);
		}

		super.hide();
	}

	/**
	 * Estimate the bounding box.
	 *
	 * @override
	 * @protected
	 */
	_estimateBoundingBox()
	{
		this._boundingBox = new PIXI.Rectangle(
			this._pos[0] - this._size[0] / 2,
			this._pos[1] - this._size[1] / 2,
			this._size[0],
			this._size[1],
		);

		// TODO take the orientation into account
	}

	/**
	 * Update the stimulus, if necessary.
	 *
	 * @protected
	 */
	_updateIfNeeded()
	{
		if (!this._needUpdate)
		{
			return;
		}
		this._needUpdate = false;

		// update the PIXI representation, if need be:
		if (this._needPixiUpdate)
		{
			this._needPixiUpdate = false;

			// if a survey div does not exist, create it:
			if (document.getElementById(this._surveyDivId) === null)
			{
				document.body.insertAdjacentHTML("beforeend", `<div id=${this._surveyDivId} class='survey' dir='auto'></div>`);
			}

			// start the survey flow:
			if (typeof this._surveyModel !== "undefined")
			{
				this._runSurveyFlow(this._surveyModel.surveyFlow);
			}
		}

		// TODO change the position, scale, anchor, z-index, etc.
		// TODO update the size, taking into account the actual size of the survey
		/*
		this._pixi.zIndex = -this._depth;
		this._pixi.alpha = this.opacity;

		// set the scale:
		const displaySize = this._getDisplaySize();
		const size_px = util.to_px(displaySize, this.units, this.win);
		const scaleX = size_px[0] / this._texture.width;
		const scaleY = size_px[1] / this._texture.height;
		this._pixi.scale.x = this.flipHoriz ? -scaleX : scaleX;
		this._pixi.scale.y = this.flipVert ? scaleY : -scaleY;

		// set the position, rotation, and anchor (image centered on pos):
		this._pixi.position = to_pixiPoint(this.pos, this.units, this.win);
		this._pixi.rotation = -this.ori * Math.PI / 180;
		this._pixi.anchor.x = 0.5;
		this._pixi.anchor.y = 0.5;
*/
	}

	/**
	 * Register custom SurveyJS expression functions.
	 *
	 * @protected
	 * @return {void}
	 */
	_registerCustomExpressionFunctions (Survey, customFuncs = [])
	{
		for (let i = 0; i < customFuncs.length; i++)
		{
			Survey.FunctionFactory.Instance.register(customFuncs[i].func.name, customFuncs[i].func, customFuncs[i].isAsync);
		}
	}

	/**
	 * Register SurveyJS widgets.
	 *
	 * @protected
	 * @return {void}
	 */
	_registerWidgets(Survey)
	{
		registerSelectBoxWidget(Survey);
		registerSliderWidget(Survey);
		registerSideBySideMatrix(Survey);
		registerMaxDiffMatrix(Survey);
		registerSliderStar(Survey);

		// load the widget style:
		// TODO
		// util.loadCss("./survey/css/widgets.css");
	}

	/**
	 * Register custom Survey properties. Usially these are relevant for different question types.
	 *
	 * @protected
	 * @return {void}
	 */
	_registerCustomSurveyProperties(Survey)
	{
		MatrixBipolar.registerSurveyProperties(Survey);
		Survey.Serializer.addProperty("signaturepad", {
			name: "maxSignatureWidth",
			type: "number",
			default: 500
		});
	}

	_registerCustomComponentCallbacks(surveyModel)
	{
		MatrixBipolar.registerModelCallbacks(surveyModel);
		DropdownExtensions.registerModelCallbacks(surveyModel);
	}

	/**
	 * Callback triggered whenever the participant answer a question.
	 *
	 * @param survey
	 * @param questionData
	 * @protected
	 */
	_onQuestionValueChanged(survey, questionData)
	{
		if (typeof this._questionAnswerTimestamps[questionData.name] === "undefined")
		{
			this._questionAnswerTimestamps[questionData.name] = {
				timestamp: 0
			};
		}
		this._questionAnswerTimestamps[questionData.name].timestamp = this._questionAnswerTimestampClock.getTime();
	}

	_composeModelWithRandomizedQuestions (surveyModel, inBlockRandomizationSettings)
	{
		let t = performance.now();
		// Qualtrics's in-block randomization ignores presense of page breaks within the block.
		// Hence creating a fresh survey data object with shuffled question order.
		let questions = [];
		let questionsMap = {};
		let newSurveyModel =
		{
			pages:[{ elements: new Array(inBlockRandomizationSettings.questionsPerPage) }]
		};
		for (let i = 0; i < surveyModel.pages.length; i++)
		{
			for (let j = 0; j < surveyModel.pages[i].elements.length; j++)
			{
				questions.push(surveyModel.pages[i].elements[j]);
				const k = questions.length - 1;
				questionsMap[questions[k].name] = questions[k];
			}
		}

		if (inBlockRandomizationSettings.layout.length > 0)
		{
			let j = 0;
			let k = 0;
			let curPage = 0;
			let curElement = 0;

			const shuffledSet0 = util.shuffle(Array.from(inBlockRandomizationSettings.set0));
			const shuffledSet1 = util.shuffle(Array.from(inBlockRandomizationSettings.set1));
			// const shuffledSet0 = this._FisherYatesShuffle(inBlockRandomizationSettings.set0);
			// const shuffledSet1 = this._FisherYatesShuffle(inBlockRandomizationSettings.set1);
			for (let i = 0; i < inBlockRandomizationSettings.layout.length; i++)
			{
				// Create new page if questionsPerPage reached.
				if (curElement === inBlockRandomizationSettings.questionsPerPage)
				{
					newSurveyModel.pages.push({ elements: new Array(inBlockRandomizationSettings.questionsPerPage) });
					curPage++;
					curElement = 0;
				}

				if (inBlockRandomizationSettings.layout[i] === "set0")
				{
					newSurveyModel.pages[curPage].elements[curElement] = questionsMap[shuffledSet0[j]];
					j++;
				}
				else if (inBlockRandomizationSettings.layout[i] === "set1")
				{
					newSurveyModel.pages[curPage].elements[curElement] = questionsMap[shuffledSet1[k]];
					k++;
				}
				else
				{
					newSurveyModel.pages[curPage].elements[curElement] = questionsMap[inBlockRandomizationSettings.layout[i]];
				}
				curElement++;
			}
		}
		else if (inBlockRandomizationSettings.showOnly > 0)
		{
			// TODO: Check if there can be questionsPerPage applicable in this case.
			const shuffledQuestions = util.shuffle(Array.from(questions));
			// shuffledQuestions = this._FisherYatesShuffle(questions);
			newSurveyModel.pages[0].elements = shuffledQuestions.splice(0, inBlockRandomizationSettings.showOnly);
		}
		else {
			// TODO: Check if there can be questionsPerPage applicable in this case.
			newSurveyModel.pages[0].elements = util.shuffle(Array.from(questions));
			// newSurveyModel.pages[0].elements = this._FisherYatesShuffle(questions);
		}
		console.log("model recomposition took", performance.now() - t);
		console.log("recomposed model:", newSurveyModel);
		return newSurveyModel;
	}

	_applyInQuestionRandomization (questionData, inQuestionRandomizationSettings, surveyData)
	{
		let choicesFieldName;
		let valueFieldName;
		if (questionData.rows !== undefined)
		{
			choicesFieldName = "rows";
			valueFieldName = "value";
		}
		else if (questionData.choices !== undefined)
		{
			choicesFieldName = "choices";
			valueFieldName = "value";
		}
		else if (questionData.items !== undefined)
		{
			choicesFieldName = "items";
			valueFieldName = "name";
		}
		else
		{
			console.log("[Survey runner]: Unknown choicesFieldName for", questionData);
		}

		if (inQuestionRandomizationSettings.randomizeAll)
		{
			questionData[choicesFieldName] = util.shuffle(Array.from(questionData[choicesFieldName]));
			// questionData[choicesFieldName] = this._FisherYatesShuffle(questionData[choicesFieldName]);
			// Handle dynamic choices.
		}
		else if (inQuestionRandomizationSettings.showOnly > 0)
		{
			questionData[choicesFieldName] = util.shuffle(Array.from(questionData[choicesFieldName]).splice(0, inQuestionRandomizationSettings.showOnly));
			// questionData[choicesFieldName] = this._FisherYatesShuffle(questionData[choicesFieldName]).splice(0, inQuestionRandomizationSettings.showOnly);
		}
		else if (inQuestionRandomizationSettings.reverse)
		{
			questionData[choicesFieldName] = Math.round(Math.random()) === 1 ? questionData[choicesFieldName].reverse() : questionData[choicesFieldName];
		}
		else if (inQuestionRandomizationSettings.layout.length > 0)
		{
			const initialChoices = questionData[choicesFieldName];
			let choicesMap = {};
			// TODO: generalize further i.e. figure out how to calculate the length of array based on availability of sets.
			const setIndices = [0, 0, 0];
			for (let i = 0; i < questionData[choicesFieldName].length; i++)
			{
				choicesMap[questionData[choicesFieldName][i][valueFieldName]] = questionData[choicesFieldName][i];
			}

			// Creating new array of choices to which we're going to write from randomized/reversed sets.
			questionData[choicesFieldName] = new Array(inQuestionRandomizationSettings.layout.length);
			const shuffledSet0 = util.shuffle(Array.from(inQuestionRandomizationSettings.set0));
			const shuffledSet1 = util.shuffle(Array.from(inQuestionRandomizationSettings.set1));
			// const shuffledSet0 = this._FisherYatesShuffle(inQuestionRandomizationSettings.set0);
			// const shuffledSet1 = this._FisherYatesShuffle(inQuestionRandomizationSettings.set1);
			const reversedSet = Math.round(Math.random()) === 1 ? inQuestionRandomizationSettings.reverseOrder.reverse() : inQuestionRandomizationSettings.reverseOrder;
			for (let i = 0; i < inQuestionRandomizationSettings.layout.length; i++)
			{
				if (inQuestionRandomizationSettings.layout[i] === "set0")
				{
					questionData[choicesFieldName][i] = choicesMap[shuffledSet0[ setIndices[0] ]];
					setIndices[0]++;
				}
				else if (inQuestionRandomizationSettings.layout[i] === "set1")
				{
					questionData[choicesFieldName][i] = choicesMap[shuffledSet1[ setIndices[1] ]];
					setIndices[1]++;
				}
				else if (inQuestionRandomizationSettings.layout[i] === "reverseOrder")
				{
					questionData[choicesFieldName][i] = choicesMap[reversedSet[ setIndices[2] ]];
					setIndices[2]++;
				}
				else
				{
					questionData[choicesFieldName][i] = choicesMap[inQuestionRandomizationSettings.layout[i]];
				}
			}

			if (inQuestionRandomizationSettings.layout.length < initialChoices.length)
			{
				// Compose unused choices set.
				// TODO: This is potentially how data loss can be avoided and thus no need to deepcopy model.
				if (surveyData.unusedChoices === undefined)
				{
					surveyData.unusedChoices = {};
				}
				surveyData.unusedChoices[questionData.name] = {
					// All other sets are always used entirely.
					set1: shuffledSet1.splice(setIndices[1], shuffledSet1.length)
				};
				console.log("unused choices", questionData.name, surveyData.unusedChoices[questionData.name]);
			}
		}

		// console.log(questionData);
	}

	/**
	 * Go over required surveyModelData and apply randomization settings.
	 * @protected
	 * @todo remove surveyData from the signature and use this._surveyModel instead!
	 */
	_processSurveyJSModel(surveyData, surveyIdx)
	{
		let newSurveyModel = undefined;

		// Qualtrics's in-block randomization ignores the presence of page breaks within the block.
		// Hence creating a fresh survey data object with shuffled question order.
		if (typeof surveyData.questionsOrderRandomization[surveyIdx] !== "undefined")
		{
			newSurveyModel = this._composeModelWithRandomizedQuestions(
				surveyData.surveys[surveyIdx],
				surveyData.questionsOrderRandomization[surveyIdx]
			);
		}

		// note: we need to check whether the survey model has a "pages" field since empty surveys do not:
		if ("pages" in surveyData.surveys[surveyIdx])
		{
			// checking whether in-question randomization needs to be applied:
			for (let i = 0; i < surveyData.surveys[surveyIdx].pages.length; ++i)
			{
				for (let j = 0; j < surveyData.surveys[surveyIdx].pages[i].elements.length; ++j)
				{
					if (typeof surveyData.inQuestionRandomization[surveyData.surveys[surveyIdx].pages[i].elements[j].name] !== "undefined")
					{
						if (typeof newSurveyModel === "undefined")
						{
							// Marking a deep copy of survey model input data, to avoid data loss if randomization returns a subset of choices.
							// TODO: think of something more optimal.
							newSurveyModel = JSON.parse(JSON.stringify(surveyData.surveys[surveyIdx]));
						}
						this._applyInQuestionRandomization(
							newSurveyModel.pages[i].elements[j],
							surveyData.inQuestionRandomization[newSurveyModel.pages[i].elements[j].name],
							surveyData
						);
					}
				}
			}
		}

		if (typeof newSurveyModel === "undefined")
		{
			// No changes were made, just return original data.
			newSurveyModel = surveyData.surveys[surveyIdx];
		}
		return newSurveyModel;
	}

	/**
	 * Callback triggered when the participant changed the page.
	 *
	 * @protected
	 */
	_onCurrentPageChanging(node, surveyJSModel, options)
	{
		console.log("_onCurrentPageChanging for node:", node);

		// if partial saving is activated, then save the results:
		if (this._saveOnPageChange)
		{
			Object.assign(this._overallSurveyResults, surveyJSModel.data);

			// TODO queue the saves?
			this.save();
		}

		if (this._lastPageSwitchHandledIdx === options.oldCurrentPage.visibleIndex)
		{
			// When surveyModel.currentPage is called from this handler, pagechange event gets triggered again.
			// Hence returning if we already handled this pagechange to avoid max callstack exceeded errors.
			return;
		}
		this._lastPageSwitchHandledIdx = options.oldCurrentPage.visibleIndex;

		const questions = surveyJSModel.getCurrentPageQuestions();

		// It is guaranteed that the question with skip logic is always last on the page.
		const lastQuestion = questions[questions.length - 1];
		const skipLogic = this._surveyModel.questionSkipLogic[lastQuestion.name];
		if (skipLogic !== undefined)
		{
			this._expressionsRunner.expressionExecutor.setExpression(skipLogic.expression);
			const result = this._expressionsRunner.run(surveyJSModel.data);
			if (result)
			{
				options.allowChanging = false;

				if (skipLogic.destination === "ENDOFSURVEY")
				{
					surveyJSModel.setCompleted();
					this._blockPromiseResolve(Survey.SURVEY_EVENT.SKIP_TO_END_OF_SURVEY);
				}
				else if (skipLogic.destination === "ENDOFBLOCK")
				{
					surveyJSModel.setCompleted();
					this._blockPromiseResolve(Survey.SURVEY_EVENT.SKIP_TO_END_OF_BLOCK);
				}
				else
				{
					// skipLogic.destination is a question within the current survey (qualtrics block).
					const targetQuestion = surveyJSModel.getQuestionByName(skipLogic.destination);
					const page = surveyJSModel.getPageByQuestion(targetQuestion);
					const pageQuestions = page.questions;
					let i;
					for (i = 0; i < pageQuestions.length; i++)
					{
						if (pageQuestions[i] === targetQuestion)
						{
							break;
						}
						pageQuestions[i].visible = false;
					}
					targetQuestion.focus();
					surveyJSModel.currentPage = page;
				}
			}
		}
	}

	/**
	 * Callback triggered when the participant has completed a SurveyJS Question Block.
	 *
	 * @param {Object} node - super-flow QUESTION_BLOCK node
	 * @param surveyJSModel	- the associated SurveyJS model
	 * @param options				- the SurveyJS model options
	 * @protected
	 */
	_onSurveyJSComplete(node, surveyJSModel, options)
	{
		console.log("_onSurveyJSComplete for node:", node);

		// note: we need to add the node title to the responses
		Object.assign(this._overallSurveyResults, surveyJSModel.data);
		node.data = surveyJSModel.data;

		// since _onCurrentPageChanging is not triggered when the surveyJS ends, we need to save here as well
		if (this._saveOnPageChange)
		{
			// TODO queue the saves?
			this.save();
		}

		let completionStatus = Survey.SURVEY_EVENT.NEXT_NODE;
		const questions = surveyJSModel.getAllQuestions();

		// It is guaranteed that the question with skip logic is always last on the page.
		const lastQuestion = questions[questions.length - 1];
		const skipLogic = this._surveyModel.questionSkipLogic[lastQuestion.name];
		if (skipLogic !== undefined)
		{
			this._expressionsRunner.expressionExecutor.setExpression(skipLogic.expression);
			const result = this._expressionsRunner.run(surveyJSModel.data);
			if (result)
			{
				if (skipLogic.destination === "ENDOFSURVEY")
				{
					completionStatus = Survey.SURVEY_EVENT.SKIP_TO_END_OF_SURVEY;
					surveyJSModel.setCompleted();
				}
				else if (skipLogic.destination === "ENDOFBLOCK")
				{
					completionStatus = Survey.SURVEY_EVENT.SKIP_TO_END_OF_BLOCK;
				}
			}
		}

		surveyJSModel.stopTimer();

		// check whether the survey was completed:
		const surveyVisibleQuestions = this._surveyJSModel.getAllQuestions(true);
		const nbAnsweredQuestions = surveyVisibleQuestions.reduce(
			(count, question) =>
			{
				// note: the response of a html, ranking, checkbox, or comment question is empty if the user
				// did not interact with it
				const type = question.getType();
				if (type === "html" ||
					type === "ranking" ||
					type === "checkbox" ||
					type === "comment" ||
					!question.isEmpty())
				{
					return count + 1;
				}
				else
				{
					return count;
				}
			},
			0
		);
		this._isCompletedAll = this._isCompletedAll && (nbAnsweredQuestions === surveyVisibleQuestions.length);
		if (this._isCompletedAll === false)
		{
			this.psychoJS.logger.warn(`Flag _isCompletedAll is false!`);
		}

		// resolve the QUESTION_BLOCK / SurveyJS promise:
		this._blockPromiseResolve(completionStatus);
	}

	/**
	 * Callback triggered when we have run through the whole flow.
	 *
	 * @protected
	 */
	_onFlowComplete()
	{
		this.isFinished = true;
		this._onFinishedCallback();
	}

	_onTextMarkdown(survey, options)
	{
		// convert MarkDown to HTML:
		const html = this._mdConverter.makeHtml(options.text);

		// sanitize the HTML code:
		let sanitizedHtml = html;
		// - the converter adds <p></p> around all text, so we strip them:
		if (sanitizedHtml.startsWith("<p>"))
		{
			sanitizedHtml = sanitizedHtml.substring(3, sanitizedHtml.length-4);
		}

		// TODO more?

		options.html = sanitizedHtml;
	}

	/**
	 * Prepare a SurveyJS survey to run the given QUESTION_BLOCK node, and start it.
	 *
	 * @param {Object} node - super-flow QUESTION_BLOCK node
	 * @param {Object} prevBlockResults
	 * @protected
	 */
	_runQuestionBlock(node, prevBlockResults)
	{
		this._lastPageSwitchHandledIdx = -1;
		let processedSurveyJSModel = this._processSurveyJSModel(this._surveyModel, node.surveyIdx);

		this._surveyJSModel = new window.Survey.Model(processedSurveyJSModel);

		// if we are returning to a previously visited node and there is data associated with it,
		// e.g. if the user presses the [Previous] button at the start of the second QUESTION_BLOCK,
		// we set the surveyJS data field, which prefills the answers:
		if ("data" in node)
		{
			this._surveyJSModel.data = node.data;
		}

		// console.log("running block: ", node.name, "variables=", this._variables, "prevBlockResults=", prevBlockResults);

		// prepare the variables:
		for (const name in this._variables)
		{
			// use non-augmented names here (i.e. not <block name>/<variable name>) to deal with variables
			// passed as URL parameters:
			this._surveyJSModel.setVariable(name, this._variables[name]);

			// also use augmented names:
			this._surveyJSModel.setVariable(`${node.name}/${name}`, this._variables[name]);
		}

		// turn the results from the previous blocks into variables for this block:
		for (const name in prevBlockResults)
		{
			// TODO it appears that if we turn results from this block into variables for this block, the result disappear
			//  from _surveyJSModel.data: why is that?!?
			// make sure that name is not a question:
			let nameIsQuestion = false;
			for (let question of this._surveyJSModel.getAllQuestions())
			{
				if (question.name === name)
				{
					nameIsQuestion = true;
					break;
				}
			}
			if (!nameIsQuestion)
			{
				this._surveyJSModel.setVariable(name, prevBlockResults[name]);
			}
		}

		// initialise the SurveyJS survey, if need be:
		if (!this._surveyJSModel.isInitialized)
		{
			console.log("Init surveyJS for node:", node, "with prevBlockResults:", prevBlockResults);

			this._registerCustomComponentCallbacks(this._surveyJSModel);
			this._surveyJSModel.onValueChanged.add(this._onQuestionValueChanged.bind(this));
			this._surveyJSModel.onCurrentPageChanging.add( (surveyJSModel, options) => this._onCurrentPageChanging(node, surveyJSModel, options) );
			this._surveyJSModel.onComplete.add( (surveyJSModel, options) => this._onSurveyJSComplete(node, surveyJSModel, options) );
			this._surveyJSModel.onTextMarkdown.add(this._onTextMarkdown.bind(this));
			this._surveyJSModel.onAfterRenderQuestion.add(this._handleAfterQuestionRender.bind(this));

			// add a [Previous] button at the start of the block, if it is not the first block:
			this._surveyJSModel.addNavigationItem({
				id: "survey_block_previous", title: "Previous", visibleIndex: 0,
				visible: new window.Survey.ComputedUpdater(() => {
					// only the first page of the survey, and not for the first block:
					return this._surveyJSModel.currentPageNo === 0 && !node.isFirstBlock;
				}),
				action: () => {
					// make sure that _onSurveyJSComplete is not called:
					this._surveyJSModel.onComplete.clear();

					// add the current SurveyJS survey's data to the overall results:
					Object.assign(this._overallSurveyResults, this._surveyJSModel.data);
					node.data = this._surveyJSModel.data;

					// move to the previous block (if there is one):
					this._blockPromiseResolve(Survey.SURVEY_EVENT.PREV_NODE);
				},
				innerCss: 'sd-btn sd-navigation__prev-btn',
			});

			this._surveyJSModel.isInitialized = true;
		}

		const jquerySurvey = {
			model: this._surveyJSModel,
			showItemsInOrder: "column",
			...this._surveyModel.surveySettings
		};

		// if the survey flow consists of several nodes and this one is not the last one
		// then replace COMPLETE by NEXT:
		if (node.surveyIdx < this._surveyModel.surveys.length - 1)
		{
			jquerySurvey.completeText = (this._surveyJSModel.pageNextText || Survey.CAPTIONS.NEXT);
		}

		// run the SurveyJS survey:
		jQuery(".survey").Survey(jquerySurvey);

		this._questionAnswerTimestampClock.reset();

		// TODO: should this be conditional?
		this._surveyJSModel.startTimer();

		// prepare a promise, which will be resolved upon completion of the SurveyJS survey:
		this._blockPromise = new Promise((res, rej) => {
			this._blockPromiseResolve = res;
			this._blockPromiseReject = rej;
		});

		return this._blockPromise;
	}

	/**
	 *
	 * @param node
	 * @param prevBlockResults
	 * @return {Promise<number>}
	 * @private
	 */
	async _runSurveyFlow(node, prevBlockResults = {})
	{
		let surveyEvent = Survey.SURVEY_EVENT.NEXT_NODE;
		// let nodeExitCode = Survey.NODE_EXIT_CODES.NORMAL;

		// CONDITIONAL: run the flow on one of thw two conditional branches
		if (node.type === Survey.NODE_TYPE.CONDITIONAL)
		{
			const dataset = Object.assign({}, this._overallSurveyResults, this._variables);
			this._expressionsRunner.expressionExecutor.setExpression(node.condition);
			if (this._expressionsRunner.run(dataset) && node.nodes[0] !== undefined)
			{
				surveyEvent = await this._runSurveyFlow(node.nodes[0], prevBlockResults);
			}
			else if (node.nodes[1] !== undefined)
			{
				surveyEvent = await this._runSurveyFlow(node.nodes[1], prevBlockResults);
			}
		}

		// RANDOMISER: we shuffle the node's children in place
		else if (node.type === Survey.NODE_TYPE.RANDOMIZER)
		{
			util.shuffle(node.nodes, Math.random, 0, node.nodes.length - 1);
		}

		// EMBEDDED_DATA
		else if (node.type === Survey.NODE_TYPE.EMBEDDED_DATA)
		{
			let t = performance.now();
			const surveyBlockData = this._surveyModel.embeddedData[node.dataIdx];
			for (let j = 0; j < surveyBlockData.length; j++)
			{
				// TODO: handle the rest data types.
				if (surveyBlockData[j].type === "Custom")
				{
					// Variable value can be an expression. Check if so and if valid - run it.
					// surveyBlockData is an array so all the variables in it are in order they were declared in Qualtrics.
					// This means this._variables is saturated gradually with the data necessary to perform a computation.
					// It's guaranteed to be there, unless there are declaration order mistakes.
					this._expressionsRunner.expressionExecutor.setExpression(surveyBlockData[j].value);
					if (this._expressionsRunner.expressionExecutor.canRun())
					{
						this._variables[surveyBlockData[j].key] = this._expressionsRunner.run(this._variables);
					}
					else
					{
						this._variables[surveyBlockData[j].key] = surveyBlockData[j].value;
					}
				}
			}
			// console.log("embedded data variables accumulation took", performance.now() - t);
		}

		else if (node.type === Survey.NODE_TYPE.ENDSURVEY)
		{
			if (this._surveyJSModel)
			{
				this._surveyJSModel.setCompleted();
			}
			console.log("EndSurvey block encountered, exiting.");
			surveyEvent = Survey.SURVEY_EVENT.SKIP_TO_END_OF_SURVEY;
			// nodeExitCode = Survey.NODE_EXIT_CODES.BREAK_FLOW;
		}

		// QUESTION_BLOCK:
		else if (node.type === Survey.NODE_TYPE.QUESTION_BLOCK)
		{
			surveyEvent = await this._runQuestionBlock(node, prevBlockResults);
			Object.assign(prevBlockResults, node.data);
			// Object.assign(prevBlockResults, this._surveyJSModel.data);

			// if (blockEvent === Survey.SURVEY_EVENT.SKIP_TO_END_OF_SURVEY)
			// {
			// 	nodeExitCode = Survey.NODE_EXIT_CODES.BREAK_FLOW;
			// }
		}

		// run through the children nodes of this node:
		if (surveyEvent === Survey.SURVEY_EVENT.NEXT_NODE &&
			node.type !== Survey.NODE_TYPE.CONDITIONAL &&
			node.nodes instanceof Array)
		// if (nodeExitCode === Survey.NODE_EXIT_CODES.NORMAL &&
		// 		node.type !== Survey.NODE_TYPE.CONDITIONAL &&
		// 		node.nodes instanceof Array)
		{
			let childIndex = 0;
			while (childIndex < node.nodes.length)
			{
				const childNode = node.nodes[childIndex];
				surveyEvent = await this._runSurveyFlow(childNode, prevBlockResults);

				if (surveyEvent === Survey.SURVEY_EVENT.SKIP_TO_END_OF_SURVEY)
				{
					break;
				}

				if (surveyEvent === Survey.SURVEY_EVENT.NEXT_NODE)
				{
					++childIndex;
				}
				else if (surveyEvent === Survey.SURVEY_EVENT.PREV_NODE && childIndex > 0)
				{
					--childIndex;
				}

				// if (nodeExitCode === Survey.NODE_EXIT_CODES.BREAK_FLOW)
				// {
				// 	break;
				// }
			}
		}

		// if we have just run through the top node, mark the whole flow as completed:
		if (node.isRootNode)
		{
			this._onFlowComplete();
		}

		return surveyEvent;
		// return nodeExitCode;
	}

	_resetState ()
	{
		this._lastPageSwitchHandledIdx = -1;
	}

	_handleWindowResize(e)
	{
		if (this._surveyJSModel)
		{
			for (let i = this._signaturePads.length - 1; i >= 0; i--)
			{
				// As of writing this (24.03.2023). SurveyJS doesn't have a proper event
				// for question being removed from nested locations, such as dynamic panel.
				// However, surveyJS will set .signaturePad property to null once the question is removed.
				// Utilising this knowledge to sync our lists.
				if (this._signaturePads[ i ].question.signaturePad)
				{
					this._signaturePads[ i ].question.signatureWidth = Math.min(
						this._signaturePads[i].question.maxSignatureWidth,
						this._signaturePads[ i ].htmlElement.getBoundingClientRect().width
					);
				}
				else
				{
					// Signature pad was removed. Syncing list.
					this._signaturePads.splice(i, 1);
				}
			}
		}
	}

	_addEventListeners()
	{
		window.addEventListener("resize", (e) => this._handleWindowResize(e));
	}

	_handleAfterQuestionRender (sender, options)
	{
		if (options.question.getType() === "signaturepad")
		{
			this._signaturePads.push(options);
			options.question.signatureWidth = Math.min(options.question.maxSignatureWidth, options.htmlElement.getBoundingClientRect().width);
		}
	}

	/**
	 * Init the SurveyJS.io library and various extensions, setup the theme.
	 *
	 * @protected
	 */
	_initSurveyJS()
	{
		// note: the Survey.js libraries must be added to the list of resources in PsychoJS.start:
		// psychoJS.start({ resources: [ {'surveyLibrary': true}, ... ], ...});

		// id of the SurveyJS html div:
		this._surveyDivId = `survey-${this._name}`;

		this._registerCustomExpressionFunctions(window.Survey, customExpressionFunctionsArray);
		this._registerWidgets(window.Survey);
		this._registerCustomSurveyProperties(window.Survey);
		this._addEventListeners();
		this._expressionsRunner = new window.Survey.ExpressionRunner();

		// setup the survey theme:
		window.Survey.Serializer.getProperty("expression", "minWidth").defaultValue = "100px";
		window.Survey.settings.minWidth = "100px";
		window.Survey.StylesManager.applyTheme("defaultV2");

		// load the desired style:
		// TODO
		// util.loadCss("./survey/css/grey_style.css");
	}

	/**
	 * Augment the model question names and variables with block names.
	 *
	 * @protected
	 */
	_augmentQuestionNames()
	{
		if (!("surveys" in this._surveyModel))
		{
			return;
		}

		let isFirstBlock = true;

		// augment all variables
		// note: we do not update variables with a / in them, since they have already been
		// augmented by the designer
		const augmentVariables = (nodeName, txt) =>
		{
			// the below regex captures any sequence of characters between { and } that does not contain /:
			return txt.replace(/\{([^\/}]+)\}/g, (match, variable) => `{${nodeName}/${variable}}`);
		};

		// go over all QUESTION_BLOCK of the surveyFlow, and update the names of the questions,
		// and the names of the variables, in the corresponding survey:
		const updateQuestionNames = (node) =>
		{
			if (node.type === "QUESTION_BLOCK")
			{
				node.isFirstBlock = isFirstBlock;
				if (isFirstBlock)
				{
					isFirstBlock = false;
				}

				// get the associated survey:
				if (("surveyIdx" in node) && (node.surveyIdx < this._surveyModel.surveys.length))
				{
					const survey = this._surveyModel.surveys[node.surveyIdx];

					// update all questions on all pages:
					if ("pages" in survey)
					{
						for (const page of survey.pages)
						{
							// go over all fields of the page, and augment all variables
							// note: this is much more generic than going over specific fields,
							// such as visibleIf, enableIf, etc.
							for (const field in page)
							{
								if (page.hasOwnProperty(field) && typeof page[field] === "string")
								{
									page[field] = augmentVariables(node.name, page[field]);
								}
							}

							// iterate over all elements:
							if ("elements" in page)
							{
								for (const element of page.elements)
								{
									// if the element has no title, set it to the name
									// (since we are going to change the name)
									if (!("title" in element))
									{
										element.title = element.name;
									}

									// augment the name of the element with the name of the block:
									if (typeof node.name !== "undefined")
									{
										element.name = `${node.name}/${element.name}`;
									}

									// go over all fields of the element, and augment all variables
									// note: this is much more generic than going over specific fields,
									// such as visibleIf, enableIf, expression, etc.
									for (const field in element)
									{
										if (element.hasOwnProperty(field) && typeof element[field] === "string")
										{
											element[field] = augmentVariables(node.name, element[field]);
										}
									}
								}
							}
						}
					}
				}
			}

			// if this node has children, iterate over all of them:
			if ("nodes" in node)
			{
				for (const childNode of node.nodes)
				{
					updateQuestionNames(childNode);
				}
			}
		};

		updateQuestionNames(this._surveyModel.surveyFlow);

		console.log("augmented surveys: ", JSON.stringify(this._surveyModel.surveys));
	}
}
